<template>
  <div v-title="$route.meta.title"
       class="asr-check-template-table">
    <div class="page-heading">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ path: '/' }">首页</el-breadcrumb-item>
        <el-breadcrumb-item>录音风险管理</el-breadcrumb-item>
        <el-breadcrumb-item>录音风险配置</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <div class="asr-check-template-table__card asr-check-template-table__card--search">
      <div class="asr-check-template-table__card-header"
           @click="searchExpanded = !searchExpanded">
        <span>搜索</span>
        <i :class="searchExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
      </div>

      <el-form v-show="searchExpanded"
               :model="search"
               ref="searchForm"
               label-width="96px"
               label-position="left"
               size="small"
               class="asr-check-template-table__search-form"
               @submit.native.prevent="onSearch()">
        <div class="asr-check-template-table__search-grid">
          <el-form-item label="配置标题"
                        prop="title">
            <el-input v-model="search.title"
                      placeholder="请输入配置标题"
                      clearable />
          </el-form-item>
          <el-form-item label="创建人"
                        prop="creator_id">
            <remote-select class="width-max"
                           clearable
                           placeholder="请输入管理员"
                           keyword-key="name"
                           v-model="search.creator_id"
                           :query-request="getBasicPlatformUsersIndex"
                           :find-request="getPlatformUsersDetail" />
          </el-form-item>
          <el-form-item label="创建时间"
                        prop="created_at">
            <el-date-picker v-model="search.created_at"
                            value-format="yyyy-MM-dd HH:mm:ss"
                            type="datetimerange"
                            :picker-options="dateTimePickerRangeOptions"
                            range-separator="至"
                            start-placeholder="开始时间"
                            end-placeholder="结束时间"
                            class="width-max"
                            :default-time="['00:00:00', '23:59:59']"
                            clearable />
          </el-form-item>
        </div>
        <div class="asr-check-template-table__search-actions">
          <el-button type="primary"
                     icon="el-icon-search"
                     native-type="submit">搜索</el-button>
          <el-button icon="el-icon-refresh"
                     @click="resetSearch()">重置</el-button>
        </div>
      </el-form>
    </div>

    <div class="asr-check-template-table__card">
      <div class="asr-check-template-table__card-header"
           @click="listExpanded = !listExpanded">
        <span>列表</span>
        <div class="asr-check-template-table__card-header-actions">
          <el-button type="text"
                     icon="el-icon-refresh"
                     class="asr-check-template-table__header-action"
                     @click.stop="onRefresh()" />
          <span class="asr-check-template-table__header-action">
            <i :class="listExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
          </span>
        </div>
      </div>

      <div v-show="listExpanded"
           class="asr-check-template-table__list-body">
        <div class="asr-check-template-table__toolbar">
          <router-link :to="'/audio-asr-check/template/create'">
            <el-button type="primary"
                       size="small">新建配置</el-button>
          </router-link>
          <el-button type="success"
                     size="small"
                     icon="sj-icon-export"
                     @click="onExportSearch">按搜索结果导出</el-button>
        </div>

        <div class="asr-check-template-table__table-wrap">
          <el-table :data="table"
                    class="asr-check-template-table__table"
                    style="width: 100%">
            <el-table-column prop="id"
                             label="配置ID"
                             width="88" />
            <el-table-column prop="title"
                             label="配置标题"
                             min-width="200"
                             show-overflow-tooltip>
              <template slot-scope="scope">
                <router-link :to="`/audio-asr-check/template/update/${scope.row.id}`">
                  <el-link type="primary">{{ scope.row.title || '-' }}</el-link>
                </router-link>
              </template>
            </el-table-column>
            <el-table-column prop="question_count"
                             label="问题数"
                             width="90" />
            <el-table-column prop="bind_count"
                             label="绑定分步模板数"
                             width="130"
                             align="center" />
            <el-table-column prop="user_name"
                             label="创建人"
                             min-width="110"
                             show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.user_name || '-' }}</template>
            </el-table-column>
            <el-table-column prop="created_at"
                             label="创建时间"
                             width="170" />
            <el-table-column prop="updated_at"
                             label="更新时间"
                             width="170" />
            <el-table-column fixed="right"
                             label="操作"
                             width="340"
                             align="left">
              <template slot-scope="scope">
                <div class="asr-check-template-table__actions">
                  <span class="asr-check-template-table__action is-edit"
                        @click="$router.push(`/audio-asr-check/template/update/${scope.row.id}`)">
                    {{ Number(scope.row.editable) === 1 ? '修改' : '查看' }}
                  </span>
                  <span class="asr-check-template-table__action is-copy"
                        @click="onCopy(scope.row)">复制</span>
                  <span class="asr-check-template-table__action is-bind"
                        @click="openBoundSubstepDialog(scope.row)">关联分步模板</span>
                  <button v-if="canDelete(scope.row)"
                          type="button"
                          class="asr-check-template-table__action is-delete"
                          @click="onDelete(scope.row)">删除</button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="pagination">
          <el-pagination background
                         @size-change="handleSizePageChange"
                         @current-change="handleCurrentPageChange"
                         :current-page="pagination.current_page"
                         :page-sizes="[20, 50, 100, 200]"
                         :page-size="pagination.per_page"
                         layout="total, sizes, prev, pager, next, jumper"
                         :total="pagination.total" />
        </div>
      </div>
    </div>

    <audio-asr-check-bound-substep-dialog
      :visible.sync="boundSubstepDialogVisible"
      :template-id="boundSubstepTemplateId"
      :template-title="boundSubstepTemplateTitle"
      @changed="onRefresh()" />
  </div>
</template>

<script>
import table, { removeEmptyParams } from '@core/mixins/table'
import dateTimePicker from '@core/mixins/dateTimePicker'
import AudioAsrCheckBoundSubstepDialog from '../components/AudioAsrCheckBoundSubstepDialog'

export default {
  components: { AudioAsrCheckBoundSubstepDialog },
  mixins: [table, dateTimePicker],
  data () {
    return {
      searchExpanded: true,
      listExpanded: true,
      search: {
        title: null,
        creator_id: null,
        created_at: [],
      },
      table: [],
      pagination: {
        current_page: 1,
        per_page: 50,
        total: 0,
      },
      searchFieldType: {
        creator_id: Number,
        created_at: Array,
      },
      boundSubstepDialogVisible: false,
      boundSubstepTemplateId: null,
      boundSubstepTemplateTitle: '',
    }
  },
  methods: {
    ...request.map([
      'getBasicPlatformUsersIndex',
      'getPlatformUsersDetail',
    ]),
    getTableData (params) {
      return request.page('queryAudioAsrCheckTemplate', params)
    },
    onExportSearch () {
      const exportParams = removeEmptyParams(_.cloneDeep(this.lastSeachParams || {}))
      delete exportParams.current_page
      delete exportParams.per_page
      delete exportParams['']
      this.$export(`${this.$route.meta.title}_搜索结果`, {
        fileHeader: [
          { key: 'id', text: '配置ID' },
          { key: 'title', text: '配置标题' },
          { key: 'question_count', text: '问题数' },
          { key: 'bind_count', text: '绑定分步模板数' },
          { key: 'user_name', text: '创建人' },
          { key: 'created_at', text: '创建时间' },
          { key: 'updated_at', text: '更新时间' },
        ],
        total: this.pagination.total,
        request: this.getTableData,
        requestParams: exportParams,
      })
    },
    resetSearch () {
      this.$refs[this.refSearchForm].resetFields()
      this.onSearch()
    },
    canDelete (row) {
      return Number(row.editable) === 1 && Number(row.bind_count) === 0
    },
    onCopy (row) {
      this.$confirm(`确认复制配置「${row.title}」吗？将生成一份新配置（不含分步模板绑定）。`, '提示', { type: 'info' }).then(() => {
        request.common('copyAudioAsrCheckTemplate', row.id).then(() => {
          this.$message.success('复制成功')
          this.onRefresh()
        })
      }).catch(() => {})
    },
    onDelete (row) {
      this.$confirm(`确认删除配置「${row.title}」吗？删除后无法恢复。`, '提示', { type: 'warning' }).then(() => {
        request.common('deleteAudioAsrCheckTemplate', row.id).then(() => {
          this.$message.success('删除成功')
          this.onRefresh()
        })
      }).catch(() => {})
    },
    openBoundSubstepDialog (row) {
      this.boundSubstepTemplateId = row.id
      this.boundSubstepTemplateTitle = row.title || ''
      this.boundSubstepDialogVisible = true
    },
  },
}
</script>

<style lang="scss" scoped>
.asr-check-template-table {
  &__card {
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    overflow: hidden;

    & + & {
      margin-top: 16px;
    }
  }

  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 24px;
    border-bottom: 1px solid #ebeef5;
    cursor: pointer;
    user-select: none;
    color: #606266;
    font-size: 14px;
    font-weight: 500;

    i {
      color: #909399;
    }
  }

  &__card-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;

    ::v-deep .asr-check-template-table__header-action.el-button {
      min-width: 24px;
      padding: 0;
      margin: 0;
    }
  }

  &__header-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    margin: 0;
    color: #909399;
    flex-shrink: 0;
    line-height: 1;

    i {
      font-size: 14px;
    }
  }

  &__search-form {
    padding: 16px 24px 20px;
  }

  &__search-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px 16px;
  }

  &__search-actions {
    margin-top: 16px;
    padding-top: 4px;
  }

  &__list-body {
    padding: 16px;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    padding: 0 0 16px;
  }

  &__table-wrap {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;
    margin-top: 0;

    ::v-deep .el-table::before,
    ::v-deep .el-table::after {
      display: none;
    }

    ::v-deep .el-table__body tr:last-child td {
      border-bottom: none;
    }
  }

  &__table {
    ::v-deep {
      th {
        background: #f5f7fa;
        color: #909399;
        font-size: 12px;
        font-weight: 500;
      }

      td {
        font-size: 14px;
        color: #303133;
      }

      .el-table__row:hover > td {
        background: #fafafa;
      }
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 4px 0;
  }

  &__action {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 18px;
    font-weight: 500;
    background: #fff;
    cursor: pointer;
    transition: background-color 0.15s ease;
    outline: none;
    font-family: inherit;

    &[type='button'] {
      appearance: none;
    }

    &.is-edit {
      color: #606266;
      border: 1px solid #909399;
    }

    &.is-copy {
      color: #059669;
      border: 1px solid #34d399;
    }

    &.is-bind {
      color: #2563eb;
      border: 1px solid #3b82f6;
    }

    &.is-delete {
      color: #dc2626;
      border: 1px solid #ef4444;
    }

    &:hover {
      background: #f9fafb;
    }
  }

  .pagination {
    margin: 16px auto 0;
    text-align: center;
  }
}

@media (max-width: 1200px) {
  .asr-check-template-table {
    &__search-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
}

@media (max-width: 768px) {
  .asr-check-template-table {
    &__search-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>

<style lang="scss">
.asr-check-template-table__search-form {
  .el-form-item {
    margin-bottom: 0;
  }

  .el-form-item__label {
    color: #606266;
    font-size: 13px;
    line-height: 32px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/audio-asr-check/pages/AudioAsrCheckTemplateTable.vue